'use strict';

import React, {Component, useState} from 'react';
import {observer} from "mobx-react";
import classnames from "classnames";

import appStore from "@uw-it-sis/lib-react/lib/AppStore";

import Button from 'react-bootstrap/Button';
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import RegToolsStore from "./RegToolsStore";
import RegToolsInjector from './RegToolsInjector';
import {NextAction} from "../lib/Constants";

const RegTools = () => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Only render the regtools in dev/eval/locally
    if (RegToolsInjector.isPreProdEnvironment()) {
        return (
            <>
                <Button variant="inline-link" onClick={handleShow}>RegTools</Button>
                <RegToolsModal show={show} handleClose={handleClose} />
            </>
        );
    } else {
        return null
    }
}
export default RegTools

/**
 * @param {Object} props
 * @param {boolean} props.show
 * @param {() => void} props.handleClose
 */
const RegToolsModal = observer((props) => {
    return (
        <Modal id="modal-regtools"
               show={props.show}
               onHide={props.handleClose}
               size="xl"
               autoFocus={false}
               aria-labelledby="modal-regtools-title"
               centered>
            <Modal.Header closeButton>
                <Modal.Title id="modal-regtools-title">Registration Tools</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h2>⚠️ <span className="text-danger" >CAUTION: HIGH VOLTAGE</span> ⚠️</h2>
                <h2>For development use ONLY.</h2>
                <p>These tools are intended to streamline <strong>UI testing</strong> of registration components. <strong>Backend testing is not supported.</strong></p>
                <p>For example, these tools can be used to verify text/behavior <strong>within</strong> the acknowledgements modal, but
                    transitions <strong>between</strong> acknowledgements and subsequent states will not reflect normal application usage.</p>
                <h4>The following disclaimers apply:</h4>
                <ul>
                    <li>RegTool settings <strong>do not</strong> modify SDB data directly. However, actions taken in the main application (e.g. submitting a form) <strong>WILL</strong> affect SDB as normal.</li>
                    <li>It may be possible to trigger an invalid application state - identified bugs should be reproducible without the use of these tools.</li>
                    <li>RegTool settings apply ONLY to the current browser tab. (Duplicated tabs will inherit settings.)</li>
                    <li>Non-student users may need to backdoor as a student.</li>
                </ul>
                <Form.Check
                    className="mb-3"
                    checked={RegToolsStore.agreeToTOS}
                    id="agreeToTOS"
                    name="agreeToTOS"
                    value="agreeToTOS"
                    type="checkbox"
                    label="I have read and understand the above disclaimers."
                    onChange={RegToolsStore.toggleAgreeToTOS}
                />
                {RegToolsStore.agreeToTOS &&
                    <Tabs
                        id="regtools-tabs"
                        onSelect={(k) => RegToolsStore.setCurrentTab(k)}
                            activeKey={RegToolsStore.currentTab}>
                            <Tab key="reg" eventKey="reg" title="Registration Flow">
                                <RegGeneralTools />
                            </Tab>
                            <Tab key="acknowledge" eventKey="acknowledge" title="Acknowledgements">
                                <AcknowledgementTools />
                            </Tab>
                            <Tab key="address" eventKey="address" title="Address">
                                <AddressTools />
                            </Tab>
                            <Tab key="optcharges" eventKey="optCharges" title="Opt Charges">
                                <OptChargesTools />
                            </Tab>
                        </Tabs>
                    }
                </Modal.Body>
                <Modal.Footer>
                    {RegToolsStore.agreeToTOS &&
                    <>
                        <Button className="me-auto" variant="outline-danger"
                            onClick={() => {
                                if (confirm("This will clear all RegTools settings and refresh the page. Continue?")) {
                                    RegToolsStore.clearSessionStores();
                                    window.location.reload();
                                }
                            }}>Clear settings</Button>
                        <Button onClick={() => window.location.reload()} variant="primary">Refresh with these settings</Button>
                    </>
                    }
                    <Button onClick={props.handleClose} variant="link">Cancel</Button>
                </Modal.Footer>
            </Modal>
        )
    })

const RegGeneralTools = observer((props) => {

    // A helper since all of the buttons are basically the same
    let NextActionRadioButton = observer((props) => {
        return (
            <Form.Check
                checked={props.id == RegToolsStore.regGeneralForm.nextAction}
                id={props.id}
                name="nextAction"
                type="radio"
                value={props.id}
                label={props.label}
                onChange={RegToolsStore.setRegGeneralFormItem}
                {...props}
            />
        )
    })

    return (
        <Form>
            <fieldset className="my-3">
                <legend className="lead fw-bold">Next Action</legend>
                <div className="d-flex flex-column gap-1">
                    <NextActionRadioButton id="unset" label="Read from SDB (default)"/>
                    <NextActionRadioButton id={NextAction.REG_SVCS_NOT_OPEN} label="Registration Services Not Open"/>
                    <NextActionRadioButton id={NextAction.ACKNOWLEDGE} label="Acknowledgements" className="d-inline-block"/>
                    <NextActionRadioButton id={NextAction.ADDRESS} label="Address/Optional Charges"/>
                    <NextActionRadioButton id={NextAction.STUDENT_REG_NOT_OPEN} label="Student Registration Not Open"/>
                    <NextActionRadioButton id={NextAction.REGISTER} label="Register"/>
                    <NextActionRadioButton id="error" label="Force an Error"/>
                </div>
            </fieldset>
            <Form.Text className="d-block">On page startup, we normally call to SDB to determine where in the registration flow to
                direct the user. Currently, this control simply overrides reg-flow state; all other user data is
                retrieved from SDB as normal unless overriden elsewhere.</Form.Text>
            <Form.Text className="d-block text-danger">⚠️ The application will continue to render the selected action until
                you return here to change it. ⚠️</Form.Text>
        </Form>
    )
})

const AcknowledgementTools = observer((props) => {
    return (
        <Form>
            <Form.Group className="mb-3" controlId="ackOverride">
                {/* <Form.Label>Enable Override</Form.Label> */}
                <Form.Check
                    checked={RegToolsStore.acknowledgeForm.override}
                    id="override"
                    name="override"
                    type="switch"
                    label="Enable Acknowledgements Overrides"
                    onChange={RegToolsStore.toggleAcknowledgeFormItem}
                />
            </Form.Group>
            <Form.Group className="mb-3" controlId="ackShowAll">
                {/* <Form.Label>Show all acknowledgements</Form.Label> */}
                <Form.Check
                    checked={RegToolsStore.acknowledgeForm.showAll}
                    id="showAll"
                    name="showAll"
                    type="switch"
                    disabled={!RegToolsStore.acknowledgeForm.override}
                    label="Show all acknowledgements"
                    onChange={RegToolsStore.toggleAcknowledgeFormItem}
                />
            </Form.Group>
            <Form.Text className="text-danger">⚠️ Upon completion, the Acknowledgements modal will enter a fallback state. A page refresh will re-trigger acknowledgements, or you can return here to change NextAction. ⚠️</Form.Text>
        </Form>
    )
})

const AddressTools = observer((props) => {
    return (
        <p>No Address tools are available at this time.</p>
    )
})

const OptChargesTools = observer((props) => {

    // Custom Label component, since it's the same for each type of control
    let CustomControlLabel = ({children, ...props}) => (
        <Form.Label
            column
            sm={2}
            className={classnames({"text-muted":!RegToolsStore.optChargesForm.override})}
            {...props}
        >
            {children}
        </Form.Label>
    )

    // Custom component for the Yes/No controls. I used radio buttons instead
    // of dropdowns but would be pretty easy to switch back.
    const YesNoControl = observer(
        /**
         * @param {Object} props
         * @param {string} props.id
         * @param {string} [props.label]
         * @param {string} [props.note]
         */
        (props) => {
        return (
            <>
                <Form.Group as={Row} className="mb-1" controlId={props.id}>
                    <CustomControlLabel>{props.label ?? props.id}</CustomControlLabel>
                    <Col sm="2">
                        <Form.Check
                            checked={"Y" === RegToolsStore.optChargesForm[props.id]}
                            disabled={!RegToolsStore.optChargesForm.override}
                            id={props.id}
                            name={props.id}
                            type="radio"
                            value="Y"
                            label="Yes"
                            onChange={RegToolsStore.setOptChargesFormItem}
                        />
                        <Form.Check
                            checked={"N" === RegToolsStore.optChargesForm[props.id]}
                            disabled={!RegToolsStore.optChargesForm.override}
                            id={props.id}
                            name={props.id}
                            type="radio"
                            value="N"
                            label="No"
                            onChange={RegToolsStore.setOptChargesFormItem}
                        />
                    </Col>
                    <Col sm="2">
                        <Form.Text> {props.note}</Form.Text>
                    </Col>
                </Form.Group>
            </>
        )
    })

    // Component for the multi-option controls
    const DropdownControl = observer(
        /**
         * @param {Object} props
         * @param {string} props.id
         * @param {string} [props.label]
         * @param {React.ReactNode} props.children
         */
        (props) => <>
            <Form.Group as={Row} className="mb-1" controlId={props.id}>
                <CustomControlLabel>{props.label ?? props.id}</CustomControlLabel>
                <Col sm="2">
                    <Form.Select
                        disabled={!RegToolsStore.optChargesForm.override}
                        onChange={RegToolsStore.setOptChargesFormItem}
                        value={RegToolsStore.optChargesForm[props.id]}
                        name={props.id}
                        id={props.id}
                    >
                        {props.children}
                    </Form.Select>
                </Col>
            </Form.Group>
        </>
    )

    // Custom component for the free-form text controls. This one accepts an
    // onChange callback, since one of the controls in the old form forces a
    // change in another control.
    const FreeFormControl = observer(
        /**
         * @param {Object} props
         * @param {string} props.id
         * @param {string} [props.label]
         * @param {string} [props.note]
         * @param {number} [props.maxLength]
         * @param {(e: any) => any} [props.onChange]
         */
        (props) => (
            <>
                <Form.Group as={Row} className="mb-1" controlId={props.id}>
                    <CustomControlLabel>{props.label ?? props.id}</CustomControlLabel>
                    <Col sm="2">
                        <Form.Control
                            disabled={!RegToolsStore.optChargesForm.override}
                            onChange={(e) => {
                                props.onChange && props.onChange(e)
                                RegToolsStore.setOptChargesFormItem(e)
                            }}
                            value={RegToolsStore.optChargesForm[props.id]}
                            name={props.id}
                            id={props.id}
                            maxLength={props.maxLength ?? -1}
                        />
                    </Col>
                    <Col sm="2">
                        <Form.Text>{props.note}</Form.Text>
                    </Col>
                </Form.Group>
            </>
        )
    )

    return (
        <Form>
            {/* Opt-charges master override */}
            <Form.Group className="mb-5" controlId="optChargesOverride">
                <Form.Check
                    checked={RegToolsStore.optChargesForm.override}
                    id="override"
                    name="override"
                    type="switch"
                    label="Enable Opt Charges Overrides"
                    onChange={RegToolsStore.toggleOptChargesFormItem}
                />
            </Form.Group>

            <YesNoControl
                id="eligible_sw"
                note="(eligible for selections?)"
                />

            <YesNoControl id="acad_year_gaip_sw" note="(class_code = 8)"/>

            <DropdownControl id="branch_code" label="Campus">
                <option value="0">Seattle</option>
                <option value="1">Bothell</option>
                <option value="2">Tacoma</option>
            </DropdownControl>

            <FreeFormControl
                id="class_code"
                maxLength={2}
                onChange={(e) => {
                    if (e.target.value == "8") {
                        RegToolsStore.setOptChargesFormItem("acad_year_gaip_sw", "N")
                    }
                }}
            />
            <FreeFormControl id="curr_ins_sel_code" maxLength={2}/>
            <FreeFormControl id="curr_ins_sel_amt" maxLength={8}/>

            <YesNoControl id="enroll_requird_sw"/>

            <FreeFormControl id="exemption_code" maxLength={2} note="(0,5-9)"/>

            <YesNoControl id="has_annual_ins_sw"/>

            <FreeFormControl id="ins_cancel_reason" maxLength={1} note="(0-6)"/>

            <DropdownControl id="ins_status_char">
                <option value="A">A - Active</option>
                <option value="C">C - Cancelled</option>
                <option value="W">W - Waived</option>
                <option value="X">X - initial_tele_date = 0</option>
            </DropdownControl>

            <DropdownControl id="ins_tier">
                <option value="0">0 - No Insurance</option>
                <option value="1">1 - SHIP (basic)</option>
                <option value="2">2 - SHIP Plus</option>
            </DropdownControl>

            <FreeFormControl id="insurance_sel" maxLength={2}/>
            <YesNoControl id="ins_update_sw"/>

            <DropdownControl id="process_code">
                <option value="0">0 - Inquiry</option>
                <option value="1">1 - Update, no change</option>
                <option value="2">2 - Update, w/changes</option>
            </DropdownControl>

            <DropdownControl id="resident_code">
                <option value="0">0 (resident)</option>
                {/* The old code has a comment, idk what it means:
                    'resident codes 1-4 should come thru as zero' */}
                <option value="5">5 (non-resident)</option>
                <option value="6">6 (non-resident)</option>
            </DropdownControl>

            <YesNoControl id="ta_ra_sw"/>

        </Form>
    )
})
